import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Loader2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import type { ReceiptPrefill } from "@/routes/_app/-components/financeiro/financeiro-v2/types";

import { ReceiptModal } from "../receipt-modal";
import { SettlementOrders } from "./settlement-orders";
import { SettlementSummary } from "./settlement-summary";

export function SettlementModal({ id, onClose }: { id: string; onClose: () => void }) {
	const [receipt, setReceipt] = useState<ReceiptPrefill | null>(null);
	const queryClient = useQueryClient();
	const { data, isLoading, isError } = useQuery(
		orpc.financeiro.reconciliation.settlement.queryOptions({ input: { external_id: id } }),
	);
	const needsBank =
		data?.status === "paid" &&
		(data.bank_transaction_id === null ||
			data.bank_status !== "posted" ||
			data.bank_removed_remote !== false ||
			data.bank_amount_cents !== data.net_cents);
	const { data: candidates, isLoading: candidatesLoading } = useQuery(
		orpc.financeiro.reconciliation.settlementCandidates.queryOptions({
			input: { external_id: id },
			enabled: needsBank,
		}),
	);
	const { mutate, isPending } = useMutation(
		orpc.financeiro.reconciliation.linkSettlement.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Entrada bancária vinculada ao repasse");
			},
			onError: () => toast.error("Essa entrada bancária não está mais disponível"),
		}),
	);

	return (
		<>
			<OverlayShell
				label="Conciliação do repasse da Rede"
				onClose={onClose}
				className="items-center justify-center p-4"
			>
				<div className="overlay-panel max-w-5xl overflow-hidden">
					<header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
						<div>
							<p className="text-xs font-semibold uppercase tracking-wider text-primary">
								Repasse da Rede
							</p>
							<h2 className="mt-1 font-display text-2xl font-semibold text-foreground">
								{data === undefined && "Conciliação do repasse"}
								{data !== undefined && FinanceiroLabels.money(data.net_cents)}
							</h2>
							{data !== undefined && (
								<p className="mt-1 text-xs text-muted-foreground">
									Pagamento de {format.date(data.settled_on)}
								</p>
							)}
						</div>
						<Button variant="ghost" size="icon" aria-label="Fechar" onClick={onClose}>
							<X />
						</Button>
					</header>

					<div className="overflow-y-auto p-5">
						{isLoading && (
							<div className="flex min-h-72 items-center justify-center text-muted-foreground">
								<Loader2 className="animate-spin" />
							</div>
						)}
						{isError && <EmptyState icon={Landmark} title="Não foi possível carregar o repasse" />}
						{data !== undefined && (
							<div className="flex flex-col gap-5">
								<SettlementSummary settlement={data} />

								{needsBank && (
									<section>
										<h3 className="text-sm font-semibold text-foreground">Entrada bancária</h3>
										<div className="mt-2 overflow-hidden rounded-lg border border-border">
											{candidatesLoading && (
												<div className="flex h-20 items-center justify-center text-muted-foreground">
													<Loader2 className="size-4 animate-spin" />
												</div>
											)}
											{!candidatesLoading && (candidates?.length ?? 0) === 0 && (
												<p className="px-4 py-5 text-xs text-muted-foreground">
													Nenhuma entrada bancária tem o mesmo valor deste repasse.
												</p>
											)}
											{candidates?.map((bank) => (
												<div key={bank.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
													<Landmark className="size-4 text-primary" />
													<div className="min-w-40 flex-1">
														<p className="text-xs font-semibold text-foreground">
															{bank.description ?? "Entrada bancária"}
														</p>
														<p className="text-xs text-muted-foreground">
															{format.date(bank.effective_on)}
														</p>
													</div>
													<p className="font-mono text-xs font-semibold text-foreground">
														{FinanceiroLabels.money(bank.amount_cents)}
													</p>
													<Button
														size="sm"
														variant="outline"
														disabled={isPending}
														onClick={() =>
															mutate({ external_id: id, bank_transaction_id: bank.id })
														}
													>
														{isPending && <Loader2 className="animate-spin" />}
														{isPending && "Vinculando"}
														{!isPending && "Vincular entrada"}
													</Button>
												</div>
											))}
										</div>
									</section>
								)}

								<section>
									<div className="flex flex-wrap items-end justify-between gap-3">
										<div>
											<h3 className="text-sm font-semibold text-foreground">
												Composição do repasse
											</h3>
											<p className="mt-1 text-xs text-muted-foreground">
												{FinanceiroLabels.money(data.gross_cents)} bruto neste repasse
											</p>
										</div>
										<div className="text-right">
											<p className="text-xs text-muted-foreground">Taxas da Rede</p>
											<p className="font-mono text-xs font-semibold text-foreground">
												{FinanceiroLabels.money(data.fee_cents)}
											</p>
										</div>
									</div>
									<div className="mt-3">
										<SettlementOrders settlement={data} onIdentify={setReceipt} />
									</div>
								</section>
							</div>
						)}
					</div>
				</div>
			</OverlayShell>

			{receipt !== null && <ReceiptModal initial={receipt} onClose={() => setReceipt(null)} />}
		</>
	);
}
